import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { chatApi } from '../api';
import { DEFAULT_LANG } from '../config/locale';

const PURPLE = '#8B5CF6';
const PURPLE_DARK = '#7C3AED';
const HEADER_BG = '#1A1A2E';

// 채팅 말풍선 아이콘 (FAB용)
const BubbleIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 13.4876 3.36093 14.891 4 16.1272L3 21L7.8728 20C9.10904 20.6391 10.5124 21 12 21Z"
      stroke="white"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// AI 아바타 (로고 톱니바퀴)
const AiAvatar = () => (
  <AvatarCircle>
    <AvatarImg src="/ai-avatar.png" alt="AI" />
  </AvatarCircle>
);

// 전송 아이콘
const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
      stroke="white"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ChatMessage {
  role: 'bot' | 'user';
  text: string;
}

interface ChatFabProps {
  lang?: 'ko' | 'en';
}

const SHOP_URL = 'https://xn--z69azfl4br08b28au7o.com/';

const ChatFab: React.FC<ChatFabProps> = ({ lang = DEFAULT_LANG }) => {
  const isKo = lang === 'ko';
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      role: 'bot',
      text: isKo
        ? '안녕하세요! 우수건설기계 AI 상담입니다.\n어떤 제품을 찾고 계신가요?'
        : 'Hello! I\'m the Woosu AI assistant.\nWhat product are you looking for?',
    },
  ]);
  const [showShopLink, setShowShopLink] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userText = input.trim();
    setInput('');
    setShowShopLink(false);

    setMessages((prev) => [...prev, { role: 'user', text: userText }]);
    setLoading(true);

    try {
      const history = messages
        .slice(-10)
        .map((m) => ({
          role: m.role === 'bot' ? 'assistant' : 'user',
          content: m.text,
        }));

      const res = await chatApi.send(userText, history, lang);
      const data = res.data;

      setMessages((prev) => [...prev, { role: 'bot', text: data.content }]);
      if (data.shop_link) {
        setShowShopLink(true);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'bot',
          text: isKo
            ? '죄송합니다. 일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
            : 'Sorry, an error occurred. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {open && (
        <ChatPanel>
          <ChatHeader>
            <HeaderLeft>
              <AiAvatar />
              <HeaderText>
                <HeaderTitle>{isKo ? 'AI 맞춤 추천' : 'AI Recommendations'}</HeaderTitle>
                <HeaderSub>{isKo ? '실시간 제품 컨설팅' : 'Live Product Consultation'}</HeaderSub>
              </HeaderText>
            </HeaderLeft>
            <CloseBtn onClick={() => setOpen(false)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </CloseBtn>
          </ChatHeader>

          <ChatBody ref={bodyRef}>
            {messages.map((msg, i) =>
              msg.role === 'bot' ? (
                <BotRow key={i}>
                  <AiAvatar />
                  <BotBubble>{msg.text}</BotBubble>
                </BotRow>
              ) : (
                <UserRow key={i}>
                  <UserBubble>{msg.text}</UserBubble>
                </UserRow>
              )
            )}
            {loading && (
              <BotRow>
                <AiAvatar />
                <BotBubble>
                  <TypingDots>
                    <span /><span /><span />
                  </TypingDots>
                </BotBubble>
              </BotRow>
            )}
            {showShopLink && (
              <ShopLinkRow>
                <ShopLinkBtn href={SHOP_URL} target="_blank" rel="noopener noreferrer">
                  {isKo ? '쇼핑몰 바로가기' : 'Visit Shop'}
                </ShopLinkBtn>
              </ShopLinkRow>
            )}
          </ChatBody>

          <ChatInputArea>
            <ChatInput
              placeholder={isKo ? '메시지를 입력해 주세요...' : 'Type a message...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <SendBtn onClick={handleSend} $active={input.trim().length > 0 && !loading}>
              <SendIcon />
            </SendBtn>
          </ChatInputArea>
        </ChatPanel>
      )}
      <FabButton onClick={() => setOpen(!open)} $open={open}>
        {open ? (
          <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        ) : (
          <BubbleIcon />
        )}
      </FabButton>
    </>
  );
};

/* ── Styled Components ── */

const FabButton = styled.button<{ $open: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${PURPLE};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(139, 92, 246, 0.45);
  z-index: 201;
  transition: transform 0.2s, background 0.2s;
  &:hover {
    transform: scale(1.08);
    background: ${PURPLE_DARK};
  }
`;

const ChatPanel = styled.div`
  position: fixed;
  bottom: 92px;
  right: 24px;
  width: 380px;
  height: 520px;
  background: ${theme.colors.white};
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  z-index: 200;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px;
  background: ${HEADER_BG};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #fff;
`;

const HeaderSub = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
`;

const CloseBtn = styled.button`
  background: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  opacity: 0.6;
  transition: opacity 0.2s;
  &:hover { opacity: 1; }
`;

const ChatBody = styled.div`
  flex: 1;
  padding: 20px 16px;
  overflow-y: auto;
  background: #FAFAFA;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BotRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const BotBubble = styled.div`
  background: #fff;
  padding: 14px 16px;
  border-radius: 4px 16px 16px 16px;
  font-size: 14px;
  line-height: 1.6;
  color: ${theme.colors.textPrimary};
  max-width: 75%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  white-space: pre-line;
`;

const UserRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const UserBubble = styled.div`
  background: ${theme.colors.primary};
  padding: 14px 16px;
  border-radius: 16px 4px 16px 16px;
  font-size: 14px;
  line-height: 1.6;
  color: #fff;
  max-width: 75%;
`;

const ChatInputArea = styled.div`
  display: flex;
  padding: 14px 16px;
  border-top: 1px solid ${theme.colors.border};
  gap: 10px;
  align-items: center;
  background: #fff;
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  font-size: 14px;
  background: #FAFAFA;
  &:focus {
    border-color: ${PURPLE};
    outline: none;
    background: #fff;
  }
`;

const SendBtn = styled.button<{ $active: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? PURPLE : '#D1D5DB')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s;
  &:hover {
    background: ${({ $active }) => ($active ? PURPLE_DARK : '#D1D5DB')};
  }
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 4px 0;
  span {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #aaa;
    animation: typingBounce 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
  @keyframes typingBounce {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-6px); opacity: 1; }
  }
`;

const ShopLinkRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 4px 0;
`;

const ShopLinkBtn = styled.a`
  display: inline-block;
  padding: 10px 24px;
  background: ${PURPLE};
  color: #fff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;
  &:hover {
    background: ${PURPLE_DARK};
  }
`;

export default ChatFab;
